import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Client } from 'app/models/client.models';
import { 
  MovimientoCuentaCorriente, 
  ResumenCuentaCorriente, 
  ApiCuentaCorrienteResponse, 
  AccountSummary, 
  VouchersByType, 
  OverdueVoucher 
} from 'app/models/cuenta-corriente.models';
import { ClientesService } from 'app/services/clientes.service';
import { ComprobantesService } from 'app/services/comprobantes.service';
import { SaldoInicialDialogComponent } from './saldo-inicial-dialog/saldo-inicial-dialog.component';
import { ConfirmarDialogComponent } from './confirmar-dialog/confirmar-dialog.component';

@Component({
  selector: 'app-resumen',
  templateUrl: './resumen.component.html',
  styleUrls: ['./resumen.component.css']
})
export class ResumenComponent implements OnInit {

  idCliente: number;
  razonSocial: string;
  totalComprobantes: number;
  totalFacturado: number;
  deuda: number;
  saldo: number;
  comprobantes = [];
  cliente: Client;
  isCliente: boolean;
  isProveedor: boolean;
  
  // Propiedades para cuenta corriente
  movimientosCuentaCorriente: MovimientoCuentaCorriente[] = [];
  resumenCuentaCorriente: ResumenCuentaCorriente | null = null;
  accountSummary: AccountSummary | null = null;
  vouchersByType: VouchersByType | null = null;
  overdueVouchers: OverdueVoucher[] = [];
  recentMovements: any[] = []; // Nueva propiedad para los movimientos reales de la API
  cargandoMovimientos = false;
  errorMovimientos = '';
  mostrarCuentaCorriente = false;

  // Propiedades para mejorar UX
  cargandoIndicadores = false;
  cargandoComprobantes = false;
  errorIndicadores = '';
  errorComprobantes = '';

  // Propiedades para filtros y estado de comprobantes
  filterType: string | null = null;
  
  // Propiedades para filtros de fecha
  startDate: Date | null = null;
  endDate: Date | null = null;

  constructor( 
    private comprobanteService: ComprobantesService,
    private clienteService: ClientesService,
    public dialog: MatDialog,
    private routerActive: ActivatedRoute,
    private _snackBar: MatSnackBar,
    private router: Router,
    private cdr: ChangeDetectorRef) {
      this.idCliente = this.routerActive.snapshot.params.id;
      this.totalComprobantes = 0;
      this.totalFacturado = 0;
      this.deuda = 0;
      this.saldo = 0;
     }

  ngOnInit(): void {
    const sidebar = <HTMLElement>document.querySelector('.navbar-brand');
    if (sidebar !== undefined) {
      sidebar.innerHTML = 'Clientes y Proveedores / Resumen';
    }
    
    // Validar si existe un idCliente
    if (this.idCliente) {
      this.getCliente();
      this.getMovimientosCuentaCorriente();
    } else {
      this._snackBar.open('No se ha proporcionado un ID de cliente válido', 'Cerrar', {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
        panelClass: ['error-snackbar']
      });
      this.router.navigate(['/clientes']);
    }
  }

  getCliente() {
    this.clienteService.getClienteById(this.idCliente).subscribe(
      (data) => {
        if (!data) {
          this._snackBar.open('Cliente no encontrado', 'Cerrar', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
            panelClass: ['error-snackbar']
          });
          this.router.navigate(['/clientes']);
          return;
        }
        
        this.razonSocial = data.companyName;
        this.cliente = data;
        
        if (this.cliente) {
          switch (this.cliente.type) {
            case 'cliente':
              this.isCliente = true;
              this.isProveedor = false;
              break;
            case 'proveedor':
              this.isCliente = false;
              this.isProveedor = true;
              break;
            case 'cliente/proveedor':
              this.isCliente = true;
              this.isProveedor = true;
              break;
            default:
              this.isCliente = false;
              this.isProveedor = false;
              break;
          }
        }
      },
      (error) => {
        console.error('Error al obtener datos del cliente:', error);
        this._snackBar.open('Error al cargar los datos del cliente', 'Cerrar', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['error-snackbar']
        });
        this.router.navigate(['/clientes']);
      }
    );
  }

  /**
   * Refresca todos los datos del cliente
   */
  refrescarDatos() {
    this.actualizarDatosCliente();
    this._snackBar.open('Datos actualizados correctamente', 'Cerrar', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['success-snackbar']
    });
  }

  /**
   * Calcula el porcentaje de saldo respecto al total facturado
   */
  getPorcentajeSaldo(): number {
    if (this.totalFacturado === 0) return 0;
    return (this.saldo / this.totalFacturado) * 100;
  }

  /**
   * Obtiene el color del badge según el tipo de cliente
   */
  getBadgeClass(): string {
    if (this.isCliente && !this.isProveedor) return 'badge-primary';
    if (this.isProveedor && !this.isCliente) return 'badge-success';
    if (this.isCliente && this.isProveedor) return 'badge-info';
    return 'badge-secondary';
  }

  /**
   * Formatear números con separadores de miles
   */
  formatNumber(value: number): string {
    return new Intl.NumberFormat('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  }

  /**
   * Exportar datos a CSV
   */
  exportarComprobantes() {
    if (!this.comprobantes || this.comprobantes.length === 0) {
      this._snackBar.open('No hay comprobantes para exportar', 'Cerrar', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
        panelClass: ['warning-snackbar']
      });
      return;
    }

    try {
      // Crear CSV con validaciones
      const csvData = this.comprobantes.map(comp => ({
        Numero: comp?.header?.number ? 
          `${comp.header.receiptBookId || ''}-${comp.header.number || ''}` : '-',
        Tipo: comp?.voucherType?.name || '-',
        Descripcion: (comp?.detail?.description || '-').replace(/[,\n\r]/g, ' '),
        FechaEmision: comp?.header?.dateFrom || comp?.createdAt || '-',
        FechaVencimiento: comp?.header?.expirationDate || '-',
        Importe: comp?.totalAmount || 0,
        Estado: comp?.status || '-'
      }));

      // Convertir a CSV string
      const csvString = this.convertToCSV(csvData);
      
      if (!csvString) {
        throw new Error('Error al generar el archivo CSV');
      }
      
      // Crear nombre de archivo seguro
      const nombreCliente = (this.razonSocial || 'cliente').replace(/[^a-zA-Z0-9]/g, '_');
      const fecha = new Date().toISOString().split('T')[0];
      const nombreArchivo = `comprobantes_${nombreCliente}_${fecha}.csv`;
      
      // Crear y descargar archivo
      const blob = new Blob(['\uFEFF' + csvString], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', nombreArchivo);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Limpiar URL para liberar memoria
        URL.revokeObjectURL(url);
        
        this._snackBar.open('Archivo CSV descargado correctamente', 'Cerrar', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['success-snackbar']
        });
      } else {
        throw new Error('La descarga de archivos no está soportada en este navegador');
      }
    } catch (error) {
      console.error('Error al exportar comprobantes:', error);
      this._snackBar.open('Error al exportar los comprobantes', 'Cerrar', {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
        panelClass: ['error-snackbar']
      });
    }
  }

  /**
   * Convertir array a CSV
   */
  private convertToCSV(data: any[]): string {
    if (!data || data.length === 0) {
      return '';
    }
    
    const header = Object.keys(data[0]).join(',');
    const rows = data.map(obj => {
      return Object.keys(obj).map(key => {
        const value = obj[key];
        // Escapar comillas y envolver en comillas si contiene coma
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',');
    });
    return [header, ...rows].join('\n');
  }

  /**
   * Exporta todos los datos del cliente a Excel
   */
  exportarDatosCliente() {
    try {
      // Preparar datos del cliente
      const datosCliente = {
        informacionBasica: {
          'ID Cliente': this.idCliente,
          'Razón Social': this.razonSocial,
          'Tipo': this.cliente?.type || '-',
          'Número': this.cliente?.number || '-',
          'Documento': this.cliente?.documentNumber || '-',
          'Email': this.cliente?.email || '-',
          'Teléfono': this.cliente?.phone || '-',
          'Celular': this.cliente?.cellphone || '-',
          'Dirección': this.cliente?.address || '-',
          'Localidad': this.cliente?.locality || '-',
          'Provincia': this.cliente?.province || '-',
          'Código Postal': this.cliente?.postalCode || '-'
        },
        resumenFinanciero: {
          'Saldo Actual': this.saldo,
          'Total Facturado': this.totalFacturado,
          'Deuda Vencida': this.deuda,
          'Total Comprobantes': this.totalComprobantes
        }
      };

      // Crear CSV con toda la información
      const csvContent = this.generarCSVCompleto(datosCliente);
      
      // Crear y descargar archivo
      const nombreCliente = (this.razonSocial || 'cliente').replace(/[^a-zA-Z0-9]/g, '_');
      const fecha = new Date().toISOString().split('T')[0];
      const nombreArchivo = `datos_cliente_${nombreCliente}_${fecha}.csv`;
      
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', nombreArchivo);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        this._snackBar.open('Datos del cliente exportados correctamente', 'Cerrar', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['success-snackbar']
        });
      }
    } catch (error) {
      console.error('Error al exportar datos del cliente:', error);
      this._snackBar.open('Error al exportar los datos del cliente', 'Cerrar', {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
        panelClass: ['error-snackbar']
      });
    }
  }

  /**
   * Genera un CSV completo con todos los datos del cliente
   */
  private generarCSVCompleto(datos: any): string {
    let csvContent = '';
    
    // Información básica
    csvContent += 'INFORMACIÓN BÁSICA\n';
    Object.keys(datos.informacionBasica).forEach(key => {
      csvContent += `${key},${datos.informacionBasica[key]}\n`;
    });
    
    csvContent += '\nRESUMEN FINANCIERO\n';
    Object.keys(datos.resumenFinanciero).forEach(key => {
      csvContent += `${key},${datos.resumenFinanciero[key]}\n`;
    });
    
    // Agregar comprobantes si existen
    if (this.comprobantes && this.comprobantes.length > 0) {
      csvContent += '\nCOMPROBANTES\n';
      csvContent += 'Número,Tipo,Descripción,Fecha Emisión,Fecha Vencimiento,Importe,Estado\n';
      
      this.comprobantes.forEach(comp => {
        const numero = comp?.header?.number ? 
          `${comp.header.receiptBookId || ''}-${comp.header.number || ''}` : '-';
        const tipo = comp?.voucherType?.name || '-';
        const descripcion = (comp?.detail?.description || '-').replace(/[,\n\r]/g, ' ');
        const fechaEmision = comp?.header?.dateFrom || comp?.createdAt || '-';
        const fechaVencimiento = comp?.header?.expirationDate || '-';
        const importe = comp?.totalAmount || 0;
        const estado = comp?.status || '-';
        
        csvContent += `${numero},${tipo},${descripcion},${fechaEmision},${fechaVencimiento},${importe},${estado}\n`;
      });
    }
    
    return csvContent;
  }

  /**
   * Imprimir resumen
   */
  imprimirResumen() {
    window.print();
  }

  irAPagos() {
    this.router.navigateByUrl(`comprobantes/recibo/pago/${this.idCliente}`);
  }

  irACobros() {
    this.router.navigateByUrl(`comprobantes/recibo/cobro/${this.idCliente}`);
  }

  irAProductosProveedor() {
    this.router.navigateByUrl(`comprobantes/recibo/cobro/${this.idCliente}`);
  }

  /**
   * Abre un diálogo para establecer el saldo inicial del cliente
   */
  establecerSaldoInicial() {
    if (!this.idCliente) {
      this._snackBar.open('No hay un cliente seleccionado', 'Cerrar', {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
        panelClass: ['error-snackbar']
      });
      return;
    }

    const dialogRef = this.dialog.open(SaldoInicialDialogComponent, {
      width: '40%',
      disableClose: true,
      autoFocus: true,
      panelClass: 'no-scroll-dialog'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        try {
          // Implementar la lógica para guardar el saldo inicial
          this.clienteService.establecerSaldoInicial(this.idCliente, parseFloat(result.monto), result.tipo, result.observaciones)
            .subscribe(
              (response) => {
                this._snackBar.open(response.message || 'Saldo inicial establecido correctamente', 'Cerrar', {
                    duration: 5000,
                    horizontalPosition: 'center',
                    verticalPosition: 'bottom',
                    panelClass: ['success-snackbar']
                  });
                // Recargar todos los datos del cliente después de establecer el saldo inicial
                this.actualizarDatosCliente();
              },
              (error) => {
                this._snackBar.open(error.error?.message || 'Error al establecer el saldo inicial', 'Cerrar', {
                  duration: 5000,
                  horizontalPosition: 'center',
                  verticalPosition: 'bottom',
                  panelClass: ['error-snackbar']
                });
              }
            );
        } catch (error) {
          console.error('Error en la solicitud:', error);
          this._snackBar.open(error.message || 'Error en la solicitud', 'Cerrar', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
            panelClass: ['error-snackbar']
          });
        }
      }
    });
  }
  
  /**
   * Actualiza todos los datos del cliente
   */
  /**
   * Actualiza todos los datos del cliente
   * Llamado después de establecer un saldo inicial o eliminar un comprobante
   */
  actualizarDatosCliente() {
    // Actualizar el cliente
    this.getCliente();
    
    // Actualizar cuenta corriente siempre para reflejar los cambios
    this.getMovimientosCuentaCorriente();
  }

  eliminar(venta: any) {
    // Mostrar diálogo de confirmación antes de eliminar
    const dialogRef = this.dialog.open(ConfirmarDialogComponent, {
      width: '350px',
      data: {
        titulo: 'Eliminar Comprobante',
        mensaje: '¿Está seguro que desea eliminar este comprobante?'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        try {
          this.comprobanteService.eliminarComprobante(venta.id).subscribe(
            (response) => {
              this._snackBar.open(response?.message || 'Comprobante eliminado correctamente', 'Cerrar', {
                duration: 5000,
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
                panelClass: ['success-snackbar']
              });
              // Actualizar todos los datos del cliente
              this.actualizarDatosCliente();
            },
            (error) => {
              console.error('Error al eliminar comprobante:', error);
              this._snackBar.open(error.error?.message || 'Error al eliminar el comprobante', 'Cerrar', {
                duration: 5000,
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
                panelClass: ['error-snackbar']
              });
            }
          );
        } catch (error) {
          console.error('Error en la solicitud:', error);
          this._snackBar.open(error.message || 'Error en la solicitud', 'Cerrar', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
            panelClass: ['error-snackbar']
          });
        }
      }
    });
  }

  /**
   * Obtiene los movimientos de cuenta corriente del cliente
   */
  getMovimientosCuentaCorriente() {
    if (!this.idCliente) return;
    
    this.cargandoMovimientos = true;
    this.errorMovimientos = '';
    
    // Llamada real a la API
    this.clienteService.getMovimientosCuentaCorriente(this.idCliente).subscribe(
      (response: ApiCuentaCorrienteResponse) => {
        if (response.success && response.data) {
          // Mapear datos de la API a nuestro formato interno
          this.accountSummary = response.data.accountSummary;
          this.vouchersByType = response.data.vouchersByType;
          this.overdueVouchers = response.data.overdueVouchers;
          
          // Asignar los movimientos reales de la API
          this.recentMovements = (response.data as any).recentMovements || [];
          
          // Crear resumen compatible con nuestra interfaz existente
          this.resumenCuentaCorriente = {
            clienteId: this.idCliente,
            saldoActual: (response.data.accountSummary as any).currentBalance,
            totalDebe: (response.data.accountSummary as any).totalDebit,
            totalHaber: (response.data.accountSummary as any).totalCredit,
            cantidadMovimientos: (response.data.accountSummary as any).totalMovements || (response.data.accountSummary as any).totalVouchers,
            movimientos: this.convertirComprobantesAMovimientos(response.data)
          };
          
          this.movimientosCuentaCorriente = this.resumenCuentaCorriente.movimientos;
          this.cargandoMovimientos = false;
          
          // Forzar detección de cambios
          this.cdr.detectChanges();
        } else {
          throw new Error('Respuesta de API inválida');
        }
      },
      err => {
        console.error('Error al obtener movimientos de cuenta corriente:', err);
        this.errorMovimientos = 'Error al cargar los movimientos de cuenta corriente';
        this.cargandoMovimientos = false;
        
        this._snackBar.open('Error al cargar los movimientos de cuenta corriente', 'Cerrar', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  /**
   * Obtiene los últimos movimientos de cuenta corriente con filtros de fecha
   * @param customerId ID del cliente
   * @param startDate Fecha de inicio (opcional)
   * @param endDate Fecha de fin (opcional)
   */
  getLastMovements(customerId?: number, startDate?: Date | string, endDate?: Date | string) {
    const clienteId = customerId || this.idCliente;
    
    if (!clienteId) {
      this._snackBar.open('No se ha proporcionado un ID de cliente válido', 'Cerrar', {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
        panelClass: ['error-snackbar']
      });
      return;
    }
    
    this.cargandoMovimientos = true;
    this.errorMovimientos = '';
    
    // Usar el nuevo método del servicio
    this.clienteService.getCustomerCurrentAccountMovements(clienteId, startDate, endDate).subscribe(
      (response: ApiCuentaCorrienteResponse) => {
        if (response.success && response.data) {
          // Mapear datos de la API a nuestro formato interno
          this.accountSummary = response.data.accountSummary;
          this.vouchersByType = response.data.vouchersByType;
          this.overdueVouchers = response.data.overdueVouchers;
          
          // Asignar los movimientos reales de la API si existen
          this.recentMovements = (response.data as any).recentMovements || [];
          
          // Crear resumen compatible con nuestra interfaz existente
          this.resumenCuentaCorriente = {
            clienteId: clienteId,
            saldoActual: response.data.accountSummary.currentBalance,
            totalDebe: response.data.accountSummary.totalDebit,
            totalHaber: response.data.accountSummary.totalCredit,
            cantidadMovimientos: response.data.accountSummary.totalVouchers,
            movimientos: this.convertirComprobantesAMovimientos(response.data)
          };
          
          this.movimientosCuentaCorriente = this.resumenCuentaCorriente.movimientos;
          this.cargandoMovimientos = false;
          
          // Forzar detección de cambios
          this.cdr.detectChanges();
          
          // Mostrar mensaje de éxito si se aplicaron filtros
          if (startDate || endDate) {
            this._snackBar.open('Movimientos filtrados correctamente', 'Cerrar', {
              duration: 3000,
              horizontalPosition: 'center',
              verticalPosition: 'bottom',
              panelClass: ['success-snackbar']
            });
          }
        } else {
          throw new Error('Respuesta de API inválida');
        }
      },
      err => {
        console.error('Error al obtener movimientos de cuenta corriente:', err);
        this.errorMovimientos = 'Error al cargar los movimientos de cuenta corriente';
        this.cargandoMovimientos = false;
        
        this._snackBar.open('Error al cargar los movimientos. Verifique su conexión.', 'Cerrar', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  /**
   * Calcula el saldo acumulado hasta un punto específico
   * @param index Índice del movimiento hasta el cual calcular el saldo
   */
  calcularSaldoAcumulado(index: number): number {
    const movimientos = this.getFilteredMovements();
    if (!movimientos || movimientos.length === 0) {
      return 0;
    }
    
    let saldoAcumulado = 0;
    for (let i = 0; i <= index; i++) {
      const movimiento = movimientos[i];
      if (movimiento.tipo === 'debe') {
        saldoAcumulado += movimiento.monto;
      } else if (movimiento.tipo === 'haber') {
        saldoAcumulado -= movimiento.monto;
      }
    }
    return saldoAcumulado;
  }

  /**
   * Verifica si un comprobante está vencido usando datos del header
   * @param header Header del voucher
   */
  isVoucherExpired(header: any): boolean {
    if (!header || !header.expirationDate) return false;
    
    const expirationDate = new Date(header.expirationDate);
    const today = new Date();
    
    // Comparar solo las fechas sin tener en cuenta la hora
    today.setHours(0, 0, 0, 0);
    expirationDate.setHours(0, 0, 0, 0);
    
    return expirationDate < today;
  }

  /**
   * Obtiene los días de vencimiento de un comprobante
   * @param header Header del voucher
   */
  getDaysOverdue(header: any): number {
    if (!this.isVoucherExpired(header)) return 0;
    
    const expirationDate = new Date(header.expirationDate);
    const today = new Date();
    
    const diffTime = today.getTime() - expirationDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  }

  /**
   * Verifica si una fecha de vencimiento ya ha pasado
   * @param dateStr Fecha en formato string (DD/MM/YYYY)
   * @returns true si la fecha ya ha pasado, false en caso contrario
   */
  isExpired(dateStr: string): boolean {
    if (!dateStr) return false;
    
    // Convertir formato DD/MM/YYYY a Date
    const parts = dateStr.split('/');
    if (parts.length !== 3) return false;
    
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Los meses en JS van de 0 a 11
    const year = parseInt(parts[2], 10);
    
    const expirationDate = new Date(year, month, day);
    const today = new Date();
    
    // Comparar solo las fechas sin tener en cuenta la hora
    today.setHours(0, 0, 0, 0);
    expirationDate.setHours(0, 0, 0, 0);
    
    return expirationDate < today;
  }



  /**
   * Convierte los datos de comprobantes de la API a movimientos de cuenta corriente
   * @param data Datos de la respuesta de la API
   */
  private convertirComprobantesAMovimientos(data: any): MovimientoCuentaCorriente[] {
    const movimientos: MovimientoCuentaCorriente[] = [];
    
    // Convertir comprobantes vencidos a movimientos
    if (data.overdueVouchers) {
      data.overdueVouchers.forEach((voucher: OverdueVoucher, index: number) => {
        movimientos.push({
          id: voucher.id,
          fecha: new Date(voucher.expirationDate),
          tipo: 'debe',
          comprobante: voucher.voucherNumber,
          descripcion: `Comprobante vencido (${voucher.daysOverdue} días)`,
          monto: voucher.totalAmount,
          clienteId: this.idCliente,
          voucherId: voucher.id
        });
      });
    }
    
    // Ordenar por fecha (más recientes primero)
    return movimientos.sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
  }

  /**
   * Obtiene la clase CSS según el estado del comprobante
   */
  getVoucherStatusClass(voucher: any): string {
    if (!voucher || !voucher.status) {
      return '';
    }

    switch (voucher.status.toUpperCase()) {
      case 'PAID':
        return 'status-paid';
      case 'OVERDUE':
        return 'status-overdue';
      case 'PENDING':
        return 'status-pending';
      default:
        return '';
    }
  }

  /**
   * Verifica si debe mostrar el ícono de advertencia
   */
  shouldShowWarningIcon(voucher: any): boolean {
    if (!voucher || !voucher.status || !voucher.header?.expirationDate) {
      return false;
    }
    
    return voucher.status.toUpperCase() === 'OVERDUE' && this.isExpired(voucher.header.expirationDate);
  }

  /**
   * Filtra los movimientos por tipo
   */
  filterMovementsByType(type: string | null) {
    this.filterType = type;
  }

  /**
   * Obtiene los movimientos filtrados
   */
  getFilteredMovements(): any[] {
    if (!this.filterType || !this.movimientosCuentaCorriente) {
      return this.movimientosCuentaCorriente || [];
    }
    
    return this.movimientosCuentaCorriente.filter(movement => 
      movement.tipo?.toLowerCase() === this.filterType.toLowerCase() ||
      movement.descripcion?.toLowerCase().includes(this.filterType.toLowerCase())
    );
  }

  /**
   * Limpia el filtro de movimientos
   */
  clearMovementFilter() {
    this.filterType = null;
    this.startDate = null;
    this.endDate = null;
    // Recargar los movimientos sin filtros
    this.getMovimientosCuentaCorriente();
  }

  /**
   * Aplica filtros de fecha a los movimientos
   * @param startDate Fecha de inicio
   * @param endDate Fecha de fin
   */
  applyDateFilter(startDate?: Date, endDate?: Date) {
    this.startDate = startDate || null;
    this.endDate = endDate || null;
    
    // Llamar al nuevo método para obtener movimientos filtrados
    this.getLastMovements(this.idCliente, this.startDate, this.endDate);
  }

  /**
   * Abre un diálogo para seleccionar rango de fechas
   */
  openDateFilterDialog() {
    this._snackBar.open('Funcionalidad de filtro por fechas en desarrollo', 'Cerrar', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['info-snackbar']
    });
  }

  /**
   * Obtiene el ícono para el tipo de movimiento
   */
  getMovementIcon(movement: any): string {
    if (!movement || !movement.descripcion) return 'description';
    
    const desc = movement.descripcion.toLowerCase();
    
    if (desc.includes('factura')) return 'receipt';
    if (desc.includes('nota de crédito')) return 'file_download';
    if (desc.includes('nota de débito')) return 'file_upload';
    if (desc.includes('recibo') || desc.includes('pago')) return 'payment';
    if (desc.includes('saldo anterior')) return 'history';
    
    return 'description';
  }

  /**
   * Obtiene el ícono para movimientos reales de la API
   */
  getMovementIconFromReal(movement: any): string {
    if (!movement || !movement.description) return 'description';
    
    const desc = movement.description.toLowerCase();
    
    if (desc.includes('factura')) return 'receipt';
    if (desc.includes('nota de crédito')) return 'file_download';
    if (desc.includes('nota de débito')) return 'file_upload';
    if (desc.includes('recibo') || desc.includes('pago')) return 'payment';
    if (desc.includes('saldo') || desc.includes('cambio')) return 'account_balance';
    
    return 'description';
  }

  /**
   * Obtiene la descripción completa del comprobante incluyendo información del header
   * @param movement Movimiento con información del voucher
   */
  getFullVoucherDescription(movement: any): string {
    let description = this.getVoucherTypeDescription(movement);
    
    // Agregar información adicional del header si está disponible
    if (movement?.voucher?.header) {
      const header = movement.voucher.header;
      
      // Agregar número del comprobante
      const numero = this.getComprobanteNumber(header);
      if (numero !== '-') {
        description += ` - ${numero}`;
      }
      
      // Agregar fecha de emisión
      if (header.dateFrom) {
        description += ` - Emitido: ${new Date(header.dateFrom).toLocaleDateString('es-AR')}`;
      }
      
      // Agregar fecha de vencimiento solo si saleCondition no es "contado"
      if (this.shouldShowExpirationDate(movement.voucher)) {
        description += ` - Vence: ${new Date(header.expirationDate).toLocaleDateString('es-AR')}`;
      }
      
      // Agregar observaciones si existen
      if (header.observations) {
        description += ` - ${header.observations}`;
      }
      
      // Agregar condición de venta si existe
      const saleCondition = header.saleCondition || movement.voucher.saleCondition;
      if (saleCondition) {
        description += ` - Condición: ${saleCondition}`;
      }
    }
    
    return description;
  }

  /**
   * Obtiene la descripción del tipo de comprobante basada en el voucherTypeCode
   */
  getVoucherTypeDescription(movement: any): string {
    // Verificar si existe voucherType.voucherTypesCode.name (estructura real de la API)
    if (movement?.voucherType?.voucherTypesCode?.name) {
      return movement.voucherType.voucherTypesCode.name;
    }
    
    // Verificar si existe voucherTypesCode y su name (estructura alternativa)
    if (movement?.voucherTypesCode?.name) {
      return movement.voucherTypesCode.name;
    }
    
    // Verificar si existe voucher.voucherTypesCode (otra estructura alternativa)
    if (movement?.voucher?.voucherTypesCode?.name) {
      return movement.voucher.voucherTypesCode.name;
    }
    
    // Verificar en el header del voucher si existe información del tipo
    if (movement?.voucher?.header?.voucherType) {
      return movement.voucher.header.voucherType;
    }
    
    // Verificar si hay información en voucher.voucherType
    if (movement?.voucher?.voucherType?.name) {
      return movement.voucher.voucherType.name;
    }
    
    // Fallback: si no existe voucherTypesCode, usar la descripción actual
    if (movement?.description) {
      return movement.description;
    }
    
    // Último fallback
    return 'Sin descripción';
  }

  /**
   * Convierte un valor string de la API a número
   * Maneja formatos malformados como "011979.0034678.43"
   */
  parseAmount(value: any): number {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    
    const valueStr = value.toString().trim();
    if (valueStr === '') return 0;
    
    // Detectar si el formato está malformado (concatenación de números)
    // Ejemplo: "011979.0034678.43" debería interpretarse como suma de partes
    if (valueStr.includes('.') && valueStr.split('.').length > 2) {
      // Es un formato malformado, intentar extraer los números válidos
      console.warn('Formato de monto malformado detectado:', valueStr);
      
      // Intentar corregir con el patrón específico primero
      const fixedAmount = this.fixMalformedAmount(valueStr);
      if (fixedAmount !== null) {
        return fixedAmount;
      }
      
      // Fallback: buscar patrones numéricos válidos
      const matches = valueStr.match(/(\d+\.?\d*)/g);
      if (matches && matches.length > 0) {
        // Si hay múltiples números, los sumamos
        const total = matches.reduce((sum, match) => {
          const num = parseFloat(match);
          return sum + (isNaN(num) ? 0 : num);
        }, 0);
        console.warn('Valor corregido:', total, 'desde:', valueStr);
        return total;
      }
    }
    
    // Procesamiento normal para formatos estándar
    const parsed = parseFloat(valueStr);
    return isNaN(parsed) ? 0 : parsed;
  }

  /**
   * Función auxiliar para detectar y corregir formatos específicos malformados
   * Ejemplo: "011979.0034678.43" -> 46657.43
   */
  private fixMalformedAmount(valueStr: string): number | null {
    // Patrón específico: números con ceros al inicio concatenados
    // "011979.0034678.43" debería ser 11979.00 + 34678.43
    const pattern1 = /^0*(\d+)\.0*(\d+)\.(\d+)$/;
    const match1 = valueStr.match(pattern1);
    
    if (match1) {
      const [, firstNum, secondNum, decimals] = match1;
      const amount1 = parseFloat(`${firstNum}.00`);
      const amount2 = parseFloat(`${secondNum}.${decimals}`);
      const total = amount1 + amount2;
      console.warn(`Patrón específico detectado: ${valueStr} -> ${amount1} + ${amount2} = ${total}`);
      return total;
    }
    
    return null;
  }

  /**
   * Verifica si un monto es mayor que cero
   */
  isAmountPositive(value: any): boolean {
    return this.parseAmount(value) > 0;
  }

  /**
   * Obtiene la clase CSS para mostrar saldos positivos en verde y negativos en rojo
   */
  getSaldoColorClass(value: string): string {
    const amount = this.parseAmount(value);
    const className = amount > 0 ? 'saldo-positivo' : amount < 0 ? 'saldo-negativo' : '';
    return className;
  }

  /**
   * Obtiene el total de movimientos del accountSummary
   */
  getTotalMovements(): number {
    if (!this.accountSummary) return 0;
    const summary = this.accountSummary as any;
    return summary.totalMovements || summary.totalVouchers || 0;
  }

  /**
   * Verifica si un movimiento tiene URL de comprobante
   */
  hasVoucherUrl(movement: any): boolean {
    return movement && movement.voucherId && movement.voucherId > 0;
  }

  /**
   * Obtiene la URL del comprobante
   */
  getVoucherUrl(movement: any): string {
    if (!this.hasVoucherUrl(movement)) {
      return '#';
    }
    
    // Construir URL del comprobante basado en el tipo
    return `/comprobantes/detalle/${movement.voucherId}`;
  }

  /**
   * Muestra el historial completo del cliente
   */
  verHistorial() {
    this._snackBar.open('Funcionalidad de historial en desarrollo', 'Cerrar', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['info-snackbar']
    });
  }

  /**
   * Obtiene el número completo del comprobante desde el header
   * @param header Header del voucher con la información del comprobante
   */
  getComprobanteNumber(header: any): string {
    if (!header) return '-';
    
    // Construir número completo: PuntoDeVenta-Número o solo Número
    let numeroCompleto = '';
    
    if (header.receiptBookId && header.number) {
      // Formato: 0001-00000123
      const puntoVenta = header.receiptBookId.toString().padStart(4, '0');
      const numero = header.number.toString().padStart(8, '0');
      numeroCompleto = `${puntoVenta}-${numero}`;
    } else if (header.number) {
      // Solo número
      numeroCompleto = header.number.toString();
    } else if (header.receiptBookId) {
      // Solo punto de venta
      numeroCompleto = `${header.receiptBookId}-`;
    } else {
      numeroCompleto = '-';
    }
    
    return numeroCompleto;
  }

  /**
   * Obtiene la condición de venta del comprobante
   * @param voucher Voucher con información del comprobante
   */
  getSaleCondition(voucher: any): string {
    if (!voucher) return '';
    
    const saleCondition = voucher.header?.saleCondition || voucher.saleCondition;
    
    if (!saleCondition) return '';
    
    // Normalizar y formatear la condición de venta
    switch (saleCondition.toLowerCase()) {
      case 'contado':
        return 'Contado';
      case 'cuenta corriente':
      case 'cta cte':
      case 'cc':
        return 'Cuenta Corriente';
      case '30 dias':
      case '30días':
      case '30 días':
        return '30 días';
      case '60 dias':
      case '60días':
      case '60 días':
        return '60 días';
      case '90 dias':
      case '90días':
      case '90 días':
        return '90 días';
      default:
        return saleCondition;
    }
  }

  /**
   * Verifica si un comprobante es de contado
   * @param voucher Voucher con información del comprobante
   */
  isContadoSale(voucher: any): boolean {
    const saleCondition = voucher?.header?.saleCondition || voucher?.saleCondition;
    return saleCondition && saleCondition.toLowerCase() === 'contado';
  }

  /**
   * Verifica si debe mostrar la fecha de vencimiento
   * Solo se muestra si saleCondition es distinto a "contado" y existe fecha de vencimiento
   * @param voucher Voucher con información del comprobante
   */
  shouldShowExpirationDate(voucher: any): boolean {
    // Verificar que el voucher y el header existan
    if (!voucher || !voucher.header) return false;
    
    // Verificar que existe fecha de vencimiento
    if (!voucher.header.expirationDate) return false;
    
    // Verificar que saleCondition sea distinto a "contado"
    const saleCondition = voucher.header.saleCondition || voucher.saleCondition;
    
    // Si no hay información de condición de venta, mostrar por defecto (asumir que no es contado)
    if (!saleCondition) return true;
    
    // Mostrar solo si no es "contado" (puede ser "cuenta corriente", "30 días", etc.)
    return saleCondition.toLowerCase() !== 'contado';
  }

  /**
   * Obtiene información adicional del comprobante desde el header
   * @param header Header del voucher
   */
  getComprobanteInfo(header: any): any {
    if (!header) return null;
    
    return {
      numero: this.getComprobanteNumber(header),
      fechaEmision: header.dateFrom || null,
      fechaVencimiento: header.expirationDate || null,
      puntoVenta: header.receiptBookId || null,
      numeroSecuencial: header.number || null,
      observaciones: header.observations || null
    };
  }

  /**
   * Obtiene movimientos de los últimos N días
   * @param days Número de días hacia atrás
   */
  getMovementsLastDays(days: number) {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    
    this.applyDateFilter(startDate, endDate);
  }

  /**
   * Obtiene movimientos del mes actual
   */
  getMovementsCurrentMonth() {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    this.applyDateFilter(startDate, endDate);
  }

  /**
   * Obtiene movimientos del mes anterior
   */
  getMovementsPreviousMonth() {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const endDate = new Date(now.getFullYear(), now.getMonth(), 0);
    
    this.applyDateFilter(startDate, endDate);
  }


}
